import type { AxiosRequestConfig, AxiosResponse } from 'axios';

import client from 'api/client';
import type {
    ConsultantLicense,
    ConsultantLicensePayload,
    ConsultantLicenseUpdatePayload,
    ConsultantProfileUpdatePayload,
    CreateConsultantPayload,
    ServiceConsultantProgram,
    ServiceConsultantsResponse,
    ServiceConsultantUpdatePayload,
    ServiceProviderAdminRow,
    ServiceProviderConsultant,
    ServiceProviderConfigResponse,
    ServiceProviderConfigUpdatePayload,
    ServiceProviderCountriesResponse,
    ServiceProviderCountriesUpdatePayload,
    ServiceProviderFeaturesResponse,
    ServiceProviderFeaturesUpdatePayload,
} from 'types';
import { ServiceProvider } from '../models/response';

export function get(
    id: string,
    customConfig?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<ServiceProvider>> {
    return client.get<ServiceProvider>(`/service_provider/${id}`, customConfig);
}

export function switchSP(id: number | string) {
    return client.put(`/switch_service_provider/${id}`);
}

export function list(): Promise<AxiosResponse<ServiceProviderAdminRow[]>> {
    return client.get<ServiceProviderAdminRow[]>('/service-providers');
}

export function getConsultants(
    serviceProviderId: number
): Promise<AxiosResponse<ServiceProviderConsultant[]>> {
    return client.get<ServiceProviderConsultant[]>(
        `/service-providers/${serviceProviderId}/consultants`
    );
}

export function createConsultant(
    serviceProviderId: number,
    payload: CreateConsultantPayload
): Promise<AxiosResponse<ServiceProviderConsultant>> {
    return client.post<ServiceProviderConsultant>(
        `/service-providers/${serviceProviderId}/consultants`,
        payload
    );
}

export function linkConsultant(
    serviceProviderId: number,
    consultantId: number
): Promise<AxiosResponse<ServiceProviderConsultant>> {
    return client.put<ServiceProviderConsultant>(
        `/service-providers/${serviceProviderId}/consultants/${consultantId}`
    );
}

export function unlinkConsultant(
    serviceProviderId: number,
    consultantId: number
): Promise<AxiosResponse<void>> {
    return client.delete<void>(
        `/service-providers/${serviceProviderId}/consultants/${consultantId}`
    );
}

export function setDefaultConsultant(
    serviceProviderId: number,
    consultantId: number
): Promise<AxiosResponse<ServiceProviderConsultant>> {
    return client.put<ServiceProviderConsultant>(
        `/service-providers/${serviceProviderId}/consultants/${consultantId}/default`
    );
}

export function removeDefaultConsultant(
    serviceProviderId: number,
    consultantId: number
): Promise<AxiosResponse<ServiceProviderConsultant>> {
    return client.delete<ServiceProviderConsultant>(
        `/service-providers/${serviceProviderId}/consultants/${consultantId}/default`
    );
}

export function getConsultant(
    serviceProviderId: number,
    consultantId: number
): Promise<AxiosResponse<ServiceProviderConsultant>> {
    return client.get<ServiceProviderConsultant>(
        `/service-providers/${serviceProviderId}/consultants/${consultantId}`
    );
}

export function updateConsultant(
    serviceProviderId: number,
    consultantId: number,
    payload: ConsultantProfileUpdatePayload
): Promise<AxiosResponse<ServiceProviderConsultant>> {
    return client.patch<ServiceProviderConsultant>(
        `/service-providers/${serviceProviderId}/consultants/${consultantId}`,
        payload
    );
}

export function addConsultantLicense(
    serviceProviderId: number,
    consultantId: number,
    payload: ConsultantLicensePayload
): Promise<AxiosResponse<ConsultantLicense>> {
    return client.post<ConsultantLicense>(
        `/service-providers/${serviceProviderId}/consultants/${consultantId}/licenses`,
        payload
    );
}

export function updateConsultantLicense(
    serviceProviderId: number,
    consultantId: number,
    licenseId: number,
    payload: ConsultantLicenseUpdatePayload
): Promise<AxiosResponse<ConsultantLicense>> {
    return client.patch<ConsultantLicense>(
        `/service-providers/${serviceProviderId}/consultants/${consultantId}/licenses/${licenseId}`,
        payload
    );
}

export function removeConsultantLicense(
    serviceProviderId: number,
    consultantId: number,
    licenseId: number
): Promise<AxiosResponse<void>> {
    return client.delete<void>(
        `/service-providers/${serviceProviderId}/consultants/${consultantId}/licenses/${licenseId}`
    );
}

export function getFeatures(id: number): Promise<AxiosResponse<ServiceProviderFeaturesResponse>> {
    return client.get<ServiceProviderFeaturesResponse>(`/service-providers/${id}/features`);
}

export function updateFeatures(
    id: number,
    payload: ServiceProviderFeaturesUpdatePayload
): Promise<AxiosResponse<ServiceProviderFeaturesResponse>> {
    return client.patch<ServiceProviderFeaturesResponse>(
        `/service-providers/${id}/features`,
        payload
    );
}

export function getCountries(id: number): Promise<AxiosResponse<ServiceProviderCountriesResponse>> {
    return client.get<ServiceProviderCountriesResponse>(`/service-providers/${id}/countries`);
}

export function updateCountries(
    id: number,
    payload: ServiceProviderCountriesUpdatePayload
): Promise<AxiosResponse<ServiceProviderCountriesResponse>> {
    return client.put<ServiceProviderCountriesResponse>(
        `/service-providers/${id}/countries`,
        payload
    );
}

export function updateConfig(
    id: number,
    payload: ServiceProviderConfigUpdatePayload
): Promise<AxiosResponse<ServiceProviderConfigResponse>> {
    return client.patch<ServiceProviderConfigResponse>(`/service-providers/${id}/config`, payload);
}

export function getServiceConsultants(
    id: number
): Promise<AxiosResponse<ServiceConsultantsResponse>> {
    return client.get<ServiceConsultantsResponse>(`/service-providers/${id}/service-consultants`);
}

export function setServiceConsultant(
    serviceProviderId: number,
    serviceId: number,
    payload: ServiceConsultantUpdatePayload
): Promise<AxiosResponse<ServiceConsultantProgram>> {
    return client.put<ServiceConsultantProgram>(
        `/service-providers/${serviceProviderId}/services/${serviceId}/consultant`,
        payload
    );
}

export function removeServiceConsultant(
    serviceProviderId: number,
    serviceId: number
): Promise<AxiosResponse<ServiceConsultantProgram>> {
    return client.delete<ServiceConsultantProgram>(
        `/service-providers/${serviceProviderId}/services/${serviceId}/consultant`
    );
}
